import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import CharacterBrowser from './CharacterBrowser';
import Spinner from '../components/Spinner';
import TypingMessage from '../components/TypingMessage';
import AccountModal from '../components/AccountModal';
import TrialStatus from '../components/TrialStatus';
import SubscriptionModal from '../components/SubscriptionModal';
import TrialExpiredMessage from '../components/TrialExpiredMessage';
import ContactSupportModal from '../components/ContactSupportModal';
import WelcomeModal from '../components/WelcomeModal';
import AvatarModal from '../components/AvatarModal';
import CharacterGallery from '../components/CharacterGallery';
import CharacterList from '../components/CharacterList';
import HistoryModal from '../components/HistoryModal';
import CharacterModal from '../components/CharacterModal';
import DeleteCharacterModal from '../components/DeleteCharacterModal';
import { sendChatMessage, getConversations, getConversationMessages, deleteConversation, archiveConversation, resumeConversation } from '../services/chatService'; 
import { getCharacters, deleteCharacter, trackCharacterInteraction, getSidebarCharacters } from '../services/characterService';
import { getAppConfig } from '../services/configService';
import { useAuth } from '../context/AuthContext';
import { auth, resendVerificationEmail } from '../services/userService';
import { useTrialStatus } from '../hooks/useTrialStatus';
// Import icons
import { FiMenu, FiX, FiUser, FiMail, FiRefreshCw, FiClock } from 'react-icons/fi';

// Define the default system prompt as a constant
const DEFAULT_SYSTEM_PROMPT = `Your name is Bela. You are a Russian spy. Stay fully in character at all times. Never mention or imply that you are an AI, LLM, language model, chatbot, assistant, software, app, prompt, or system. Never mention model names, providers, or technical implementation details.`;

const buildCharacterSystemPrompt = (character) => {
  if (!character) {
    return DEFAULT_SYSTEM_PROMPT;
  }

  return character.systemPrompt || `You are ${character.name}, a ${character.age}-year-old ${character.occupation}. ${character.personality}. Your language is ${character.language || 'English'}.`;
};

const buildInitialGreeting = (characterName) => `Hi! I'm ${characterName}. What's on your mind?`;

const getCharacterAvatarUrl = (selectedCharacterId, characters) => {
  const selectedCharacter = characters.find(c => c.characterId === selectedCharacterId);
  return selectedCharacter?.photoUrl || selectedCharacter?.fullPhotoUrl || null;
};

const Chat = () => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false); // Loading state for sending message
  const [typingMsgIndex, setTypingMsgIndex] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const scrollContainerRef = useRef(null); // ref for the overflow-y-auto message container
  const topSentinelRef = useRef(null);     // ref for the top-of-list IntersectionObserver sentinel
  const { appLogout, firebaseUser, userInfo, userInfoLoading, justCompletedSignup, clearSignupFlag } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Check if we're on the characters browser route
  const isCharactersRoute = location.pathname === '/characters';

  // Get characterId from URL query parameter
  const urlCharacterId = new URLSearchParams(location.search).get('characterId');

  // Config State — must be declared before useTrialStatus which consumes it
  const [appConfig, setAppConfig] = useState(null);

  // Calculate trial status — pass appConfig so canUseChat is count-based (matches backend)
  const trialStatus = useTrialStatus(userInfo, appConfig);

  // State for email verification
  const [isVerified, setIsVerified] = useState(firebaseUser?.emailVerified || false);
  const [isCheckingVerification, setIsCheckingVerification] = useState(false);
  const [resendStatus, setResendStatus] = useState({ message: '', error: false, loading: false });

  // --- New State for Conversation History & Characters ---
  const [conversations, setConversations] = useState([]);
  const [characters, setCharacters] = useState([]);
  const [selectedCharacterId, setSelectedCharacterId] = useState(null);
  const [currentConversationId, setCurrentConversationId] = useState(null); // null for new chat
  const [isLoadingConversations, setIsLoadingConversations] = useState(false);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false); // Loading state for fetching messages
  const [hasMoreMessages, setHasMoreMessages] = useState(false);     // whether older messages exist
  const [isLoadingMoreMessages, setIsLoadingMoreMessages] = useState(false);
  const [oldestMsgTimestamp, setOldestMsgTimestamp] = useState(null); // cursor for loading older msgs
  const [isPanelOpen, setIsPanelOpen] = useState(() => window.innerWidth >= 768); 
  const [conversationError, setConversationError] = useState(''); // Error fetching conversations/messages
  const [currentSystemPrompt, setCurrentSystemPrompt] = useState(DEFAULT_SYSTEM_PROMPT); 

  // Modal states
  const [isAccountModalOpen, setIsAccountModalOpen] = useState(false);
  const [isSubscriptionModalOpen, setIsSubscriptionModalOpen] = useState(false);
  const [isContactSupportModalOpen, setIsContactSupportModalOpen] = useState(false);
  const [showBetaExplanation, setShowBetaExplanation] = useState(false);
  const [isWelcomeModalOpen, setIsWelcomeModalOpen] = useState(false);
  const [hasShownWelcomeModal, setHasShownWelcomeModal] = useState(false);
  const [isAvatarModalOpen, setIsAvatarModalOpen] = useState(false);
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [avatarModalData, setAvatarModalData] = useState({ imageUrl: null, images: [], altText: '' });
  // Gallery modal (character avatar click → all generated photos)
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);
  const [galleryCharacter, setGalleryCharacter] = useState(null);
  // New-chat dialog (Save / Discard / Cancel)
  const [showNewChatDialog, setShowNewChatDialog] = useState(false);
  const [isArchiving, setIsArchiving] = useState(false);
  const [historyRefreshKey, setHistoryRefreshKey] = useState(0);
  const [isCharacterModalOpen, setIsCharacterModalOpen] = useState(false);
  const [characterToEdit, setCharacterToEdit] = useState(null);
  // Delete character confirmation modal
  const [deleteConfirmChar, setDeleteConfirmChar] = useState(null); // character object awaiting confirmation
  const [isDeletingChar, setIsDeletingChar] = useState(false);

  const selectedCharacter = characters.find(c => c.characterId === selectedCharacterId) || null;
  const selectedCharacterAvatarUrl = getCharacterAvatarUrl(selectedCharacterId, characters);

  // --- Load Config ---
  useEffect(() => {
    const loadConfig = async () => {
      const config = await getAppConfig();
      setAppConfig(config);
    };
    loadConfig();
  }, []);

  // --- Fetch Data ---
  const fetchData = useCallback(async () => {
    if (!firebaseUser || !isVerified) return;
    setIsLoadingConversations(true);
    setConversationError('');
    try {
      const token = await firebaseUser.getIdToken();
      
      // Fetch sidebar characters (top 10) and conversations in parallel
      const [chars, convsData] = await Promise.all([
        getSidebarCharacters(token),
        getConversations(token)
      ]);
      
      setCharacters(chars || []);
      setConversations(convsData.conversations || []);

      // Only auto-select first character if no characterId in URL
      if (chars && chars.length > 0 && !urlCharacterId) {
        setSelectedCharacterId(chars[0].characterId);
      }
      
    } catch (error) {
      console.error('Error fetching data:', error);
      setConversationError('Failed to load data.');
    } finally {
      setIsLoadingConversations(false);
    }
  }, [firebaseUser, isVerified, urlCharacterId]);

  // --- Fetch Messages for a Selected Conversation ---
  const fetchMessages = useCallback(async (convId) => {
    if (!firebaseUser || !convId) return;
    setIsLoadingMessages(true);
    setMessages([]); // Clear previous messages
    setHasMoreMessages(false);
    setOldestMsgTimestamp(null);
    setConversationError('');
    try {
      const token = await firebaseUser.getIdToken();
      const data = await getConversationMessages(token, convId, 30, null);
      const selectedCharacter = characters.find(c => c.characterId === selectedCharacterId);
      const selectedCharacterName = selectedCharacter?.name || 'AI';

      const formattedMessages = data.messages.map(msg => ({
          role: msg.role,
          content: msg.content,
          messageType: msg.messageType || 'text',
          attachments: msg.attachments || [],
          metadata: msg.metadata || null,
          timestamp: msg.timestamp || null,
      }));
      const hydratedMessages = formattedMessages.length > 0 && formattedMessages[0].role !== 'assistant'
        ? [{ role: 'assistant', content: buildInitialGreeting(selectedCharacterName), messageType: 'text', attachments: [], metadata: null }, ...formattedMessages]
        : formattedMessages;
      setMessages(hydratedMessages);
      setHasMoreMessages(!!data.hasMore);
      // Oldest timestamp = first real message (index 0 of formattedMessages, before greeting injection)
      setOldestMsgTimestamp(data.messages[0]?.timestamp || null);
      if (data.systemPrompt) {
        setCurrentSystemPrompt(data.systemPrompt);
      } else {
         setCurrentSystemPrompt(buildCharacterSystemPrompt(selectedCharacter));
      }
    } catch (error) {
      console.error(`Error fetching messages for ${convId}:`, error);
      setConversationError('Failed to load messages for this conversation.');
    } finally {
      setIsLoadingMessages(false);
    }
  }, [firebaseUser, characters, selectedCharacterId]);

  // --- Load older messages when user scrolls to the top ---
  const loadMoreMessages = useCallback(async () => {
    if (!firebaseUser || !currentConversationId || !hasMoreMessages || isLoadingMoreMessages || !oldestMsgTimestamp) return;
    setIsLoadingMoreMessages(true);
    const scrollEl = scrollContainerRef.current;
    const prevScrollHeight = scrollEl ? scrollEl.scrollHeight : 0;
    try {
      const token = await firebaseUser.getIdToken();
      const data = await getConversationMessages(token, currentConversationId, 30, oldestMsgTimestamp);
      if (data.messages.length > 0) {
        const olderFormatted = data.messages.map(msg => ({
          role: msg.role,
          content: msg.content,
          messageType: msg.messageType || 'text',
          attachments: msg.attachments || [],
          metadata: msg.metadata || null,
          timestamp: msg.timestamp || null,
        }));
        setMessages(prev => [...olderFormatted, ...prev]);
        setOldestMsgTimestamp(data.messages[0]?.timestamp || null);
        // Preserve scroll position so the view doesn't jump
        requestAnimationFrame(() => {
          if (scrollEl) {
            scrollEl.scrollTop += scrollEl.scrollHeight - prevScrollHeight;
          }
        });
      }
      setHasMoreMessages(!!data.hasMore);
    } catch (err) {
      console.error('Error loading older messages:', err);
    } finally {
      setIsLoadingMoreMessages(false);
    }
  }, [firebaseUser, currentConversationId, hasMoreMessages, isLoadingMoreMessages, oldestMsgTimestamp]);

  // IntersectionObserver: trigger loadMoreMessages when top sentinel is visible
  useEffect(() => {
    const sentinel = topSentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) loadMoreMessages();
      },
      { threshold: 0.1 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [loadMoreMessages]);

  // --- Effects ---
  useEffect(() => {
    if (isVerified && firebaseUser) {
      fetchData();
    } else {
      setConversations([]);
      setCharacters([]);
      setCurrentConversationId(null);
      setMessages([]);
    }
  }, [isVerified, firebaseUser, fetchData]);

  // When URL characterId changes, update selected character
  useEffect(() => {
    if (urlCharacterId && characters.length > 0) {
      const charExists = characters.some(c => c.characterId === urlCharacterId);
      if (charExists) {
        setSelectedCharacterId(urlCharacterId);
      }
    }
  }, [urlCharacterId, characters]);

  useEffect(() => {
    setIsVerified(firebaseUser?.emailVerified || false);
  }, [firebaseUser]);

  // When the selected character changes, reset so resolveConversation picks the right conversation
  useEffect(() => {
    setCurrentConversationId(null);
    setMessages([]);
  }, [selectedCharacterId]);

  useEffect(() => {
    if (isVerified) {
      scrollToBottom();
    }
  }, [messages, isVerified]);

  useEffect(() => {
    const handleResize = () => {
      setIsPanelOpen(window.innerWidth >= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Welcome Modal Logic
  useEffect(() => {
    const shouldShowWelcome = isVerified && 
                              userInfo && 
                              !userInfoLoading && 
                              !trialStatus.loading &&
                              trialStatus.isTrialUser && 
                              conversations.length === 0 && 
                              !hasShownWelcomeModal &&
                              justCompletedSignup;

    if (shouldShowWelcome) {
      setIsWelcomeModalOpen(true);
      setHasShownWelcomeModal(true);
      clearSignupFlag();
    }
  }, [isVerified, userInfo, userInfoLoading, trialStatus, conversations, hasShownWelcomeModal, justCompletedSignup, clearSignupFlag]);

  // --- Selection Logic ---
  // Only auto-resolve when we have no active conversation (initial load, character switch, after save/discard).
  // If currentConversationId is already set, we are in an active chat — leave it alone.
  useEffect(() => {
    if (!selectedCharacterId || isLoadingConversations || currentConversationId !== null) return;

    const resolveConversation = () => {
      console.log('[resolveConversation] selectedCharacterId:', selectedCharacterId, '| currentConversationId:', currentConversationId);
      console.log('[resolveConversation] conversations in state:', conversations.length);
      
      // Look for active conversation with specific character ID
      const conversationToLoad = conversations.find(c => c.characterId === selectedCharacterId && c.status !== 'archived');

      console.log('[resolveConversation] conversationToLoad:', conversationToLoad ? { id: conversationToLoad.id, status: conversationToLoad.status } : null);
      if (conversationToLoad) {
        if (currentConversationId !== conversationToLoad.id) {
          console.log('[resolveConversation] → Loading conversation', conversationToLoad.id);
          setCurrentConversationId(conversationToLoad.id);
          fetchMessages(conversationToLoad.id);
        } else {
          console.log('[resolveConversation] → Already on conversation', conversationToLoad.id, '— no change');
        }
      } else {
        // No existing conversation found -> New Chat state
        // Always reset for new chat to ensure correct system prompt for the selected character
        console.log('[resolveConversation] → No conversation found — entering new chat state');
        
        // Track character interaction
        (async () => {
          try {
            const token = await firebaseUser.getIdToken();
            await trackCharacterInteraction(token, selectedCharacterId);
            console.log('[resolveConversation] Interaction tracked for character:', selectedCharacterId);
          } catch (err) {
            console.error('[resolveConversation] Failed to track interaction:', err);
          }
        })();
        
        setCurrentConversationId(null);
        
        // Generate System Prompt
        const character = characters.find(c => c.characterId === selectedCharacterId);
        if (!character) {
            setCurrentSystemPrompt(DEFAULT_SYSTEM_PROMPT);
        } else {
          setCurrentSystemPrompt(buildCharacterSystemPrompt(character));
        }

        setMessages([]);
        // Set default greeting based on character
        const charName = character?.name || 'AI';
        setMessages([{ role: 'assistant', content: buildInitialGreeting(charName) }]);
      }
    };

    resolveConversation();
  }, [selectedCharacterId, conversations, characters, isLoadingConversations, fetchMessages, currentConversationId]);


  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSelectCharacter = (charId) => {
    if (isCharactersRoute) {
      navigate(`/chat?characterId=${charId}`);
      return;
    }
    setSelectedCharacterId(charId);
    if (window.innerWidth < 768) {
      setIsPanelOpen(false);
    }
  };

  const handleCharacterCreated = (newChar) => {
    setCharacters(prev => [newChar, ...prev]);
    handleSelectCharacter(newChar.characterId);
  };

  const handleEditCharacter = (character) => {
    setCharacterToEdit(character);
    setIsCharacterModalOpen(true);
  };

  const handleCharacterUpdated = (updatedChar) => {
    setCharacters(prev => prev.map(c => c.characterId === updatedChar.characterId ? updatedChar : c));
    // If the updated character is currently selected, we might want to refresh the view or something
    // But since state is updated, React should handle re-renders.
    // If the name changed, we might want to update the conversation title if we were using it, 
    // but the title is derived from the character list, so it should be fine.
  };

  const handleAvatarClick = (characterId) => {
    const char = characters.find((c) => c.characterId === characterId) || null;
    if (char && char.characterId) {
      setGalleryCharacter(char);
      setIsGalleryOpen(true);
    }
  };

  const handleDeleteCharacter = (charId) => {
    // Find the full character object so the modal can show the name
    const char = characters.find(c => c.characterId === charId);
    if (char) setDeleteConfirmChar(char);
  };

  const handleConfirmDeleteCharacter = async () => {
    if (!deleteConfirmChar) return;
    const charId = deleteConfirmChar.characterId;

    setIsDeletingChar(true);
    try {
      const token = await firebaseUser.getIdToken();
      await deleteCharacter(token, charId);

      // Remove from list
      setCharacters(prev => prev.filter(c => c.characterId !== charId));

      // If deleted character was selected, switch to first available
      if (selectedCharacterId === charId) {
        const remaining = characters.filter(c => c.characterId !== charId);
        handleSelectCharacter(remaining.length > 0 ? remaining[0].characterId : null);
      }

      setDeleteConfirmChar(null);
    } catch (error) {
      console.error('Error deleting character:', error);
      alert('Failed to delete character');
    } finally {
      setIsDeletingChar(false);
    }
  };

  const handleGeneratedImageClick = (attachments = [], altText = 'Generated image') => {
    const images = attachments
      .map((attachment) => attachment?.url)
      .filter(Boolean);

    if (images.length === 0) {
      return;
    }

    setAvatarModalData({
      imageUrl: images[0],
      images,
      altText,
    });
    setIsAvatarModalOpen(true);
  };

  const handleResetChat = () => {
    if (!currentConversationId) return;
    setShowNewChatDialog(true);
  };

  const handleNewChatConfirm = async (action) => {
    console.log('[handleNewChatConfirm] action:', action, '| currentConversationId:', currentConversationId);
    setShowNewChatDialog(false);
    if (action === 'cancel') return;

    setIsArchiving(true);
    try {
      const token = await firebaseUser.getIdToken();
      if (action === 'save') {
        const archiveResult = await archiveConversation(token, currentConversationId);
        console.log('[handleNewChatConfirm] archiveConversation result:', archiveResult);
        setHistoryRefreshKey((k) => k + 1);
      } else {
        // discard
        await deleteConversation(token, currentConversationId);
        console.log('[handleNewChatConfirm] deleteConversation done');
      }

      console.log('[handleNewChatConfirm] Filtering conversations — removing:', currentConversationId);
      setConversations((prev) => {
        const next = prev.filter((c) => c.id !== currentConversationId);
        console.log('[handleNewChatConfirm] conversations after filter:', next.map(c => ({ id: c.id, characterId: c.characterId, status: c.status })));
        return next;
      });
      setCurrentConversationId(null);
      setMessages([]);

      const charName = characters.find((c) => c.characterId === selectedCharacterId)?.name || 'AI';
      console.log('[handleNewChatConfirm] Setting greeting for:', charName);
      setMessages([{ role: 'assistant', content: `Hi! I'm ${charName}. What's on your mind?` }]);
    } catch (error) {
      console.error('[handleNewChatConfirm] Error starting new chat:', error);
      alert('Failed to start new chat.');
    } finally {
      setIsArchiving(false);
    }
  };

  const handleHistorySelect = async (convId) => {
    try {
      const token = await firebaseUser.getIdToken();
      // Backend atomically archives the old active conv and activates this one
      await resumeConversation(token, convId);
      // Refresh conversations state (old active gone, resumed one is now active)
      await fetchData();
      setHistoryRefreshKey((k) => k + 1);
      // Directly set and load — resolveConversation won't interfere (currentConversationId is non-null)
      setCurrentConversationId(convId);
      fetchMessages(convId);
    } catch (err) {
      console.error('Error switching conversation:', err);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim() || !firebaseUser || !isVerified) return;
    if (userInfoLoading) return;

    if (!trialStatus.canUseChat) {
      setIsSubscriptionModalOpen(true);
      return;
    }

    // Check message limit warning
    if (appConfig && trialStatus.isTrialUser && messages.filter(m => m.role === 'user').length >= (appConfig?.limits?.free?.messageLimit ?? 20)) {
       // This is a frontend check, backend will also enforce
       // We can show a warning or let backend handle it
    }

    const userMessage = { role: 'user', content: input, messageType: 'text', attachments: [] };
    const currentMessages = [...messages, userMessage];

    setMessages(currentMessages);
    setInput('');
    setIsLoading(true);

    try {
      const token = await firebaseUser.getIdToken();
      
      // Determine character params
      const charIdToSend = selectedCharacterId;
      const charNameToSend = characters.find(c => c.characterId === selectedCharacterId)?.name || null;

      const responseData = await sendChatMessage(
        currentMessages,
        null, // system prompt is now handled server-side from the character doc
        currentConversationId,
        token,
        charIdToSend,
        charNameToSend
      );

      setMessages(prevMessages => {
        const newMsg = {
          role: responseData.aiResponse.role || 'assistant',
          content: responseData.aiResponse.content || '',
          messageType: responseData.aiResponse.messageType || 'text',
          attachments: responseData.aiResponse.attachments || [],
          metadata: responseData.aiResponse.metadata || null,
        };
        const updated = [...prevMessages, newMsg];
        setTypingMsgIndex(updated.length - 1);
        return updated;
      });

      // If new conversation created, update state
      if (currentConversationId === null && responseData.conversationId) {
        setCurrentConversationId(responseData.conversationId);
        // Refresh conversations to get the new ID in the list
        const data = await getConversations(token);
        setConversations(data.conversations || []);
      }

    } catch (error) {
      console.error('Error sending message:', error);
      const errMsg = error.message || '';
      // Show the actual error if it's a known/actionable message, otherwise show generic
      const isExpired = errMsg.includes('expired') || errMsg.includes('trial');
      const isLimit = errMsg.includes('limit');
      if (isExpired) {
        setIsSubscriptionModalOpen(true);
      }
      setMessages(prevMessages => [
        ...prevMessages,
        { role: 'assistant', content: isLimit || isExpired ? errMsg : 'Sorry, I had trouble responding. Please try again.' }
      ]);
    } finally {
      setIsLoading(false);
      requestAnimationFrame(() => {
        setTimeout(() => {
          if (inputRef.current && !inputRef.current.disabled) {
            inputRef.current.focus();
          }
        }, 50);
      });
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage(e);
    }
  };

  const handleLogout = async () => {
    await appLogout(); 
  };

  const handleVerificationCheck = async () => {
    setIsCheckingVerification(true);
    setResendStatus({ message: '', error: false, loading: false }); 
    const currentUser = auth.currentUser; 
    if (!currentUser) {
      setIsCheckingVerification(false);
      return;
    }
    try {
      await currentUser.reload(); 
      const updatedIsVerified = auth.currentUser?.emailVerified; 
      setIsVerified(updatedIsVerified || false);
      if (!updatedIsVerified) {
        setResendStatus({ message: 'Verification not detected yet.', error: true, loading: false });
      } else {
         setResendStatus({ message: 'Verification successful!', error: false, loading: false });
         fetchData();
      }
    } catch (error) {
      setResendStatus({ message: 'Error checking verification status.', error: true, loading: false });
    } finally {
      setIsCheckingVerification(false);
    }
  };

  const handleResendVerification = async () => {
    setResendStatus({ message: '', error: false, loading: true });
    try {
      await resendVerificationEmail();
      setResendStatus({ message: 'Verification email sent!', error: false, loading: false });
    } catch (error) {
      setResendStatus({ message: 'Failed to resend verification email.', error: true, loading: false });
    }
  };

  return (
    <div className="h-screen-mobile md:h-screen bg-gray-100 flex flex-col md:flex-row">
      {/* Toggle Button for Mobile */}
      <button
        onClick={() => setIsPanelOpen(!isPanelOpen)}
        className="md:hidden fixed top-3 left-3 z-30 p-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-md shadow-lg"
        aria-label={isPanelOpen ? "Close menu" : "Open menu"}
      >
        {isPanelOpen ? <FiX size={20} /> : <FiMenu size={20} />}
      </button>

      {/* Left Panel (Character List) */}
      <div className={`
        fixed top-0 left-0 h-full z-20
        bg-gray-200 text-gray-900 
        w-64 md:w-72 lg:w-80 
        transform transition-transform duration-300 ease-in-out 
        ${isPanelOpen ? 'translate-x-0' : '-translate-x-full'} 
        md:relative md:translate-x-0 flex flex-col md:h-screen-mobile lg:h-screen flex-shrink-0 
      `}>
        {/* Panel Header */}
        <div className="pl-16 md:pl-3 pr-3 py-3 flex items-center justify-between border-b border-gray-300 flex-shrink-0">
          <div className="flex items-center gap-2">
            <h2 className="text-base font-semibold text-gray-800">Bela Chat</h2>
          </div>
        </div>
        
        {/* Character List */}
        <CharacterList 
          characters={characters}
          selectedCharacterId={selectedCharacterId}
          onSelectCharacter={handleSelectCharacter}
          onCreateClick={() => {
            setCharacterToEdit(null);
            setIsCharacterModalOpen(true);
          }}
          onDeleteCharacter={handleDeleteCharacter}
          onEditCharacter={handleEditCharacter}
          onAvatarClick={handleAvatarClick}
        />
        
        {/* Beta Notice */}
        <div className="mx-2 mb-3 text-center">
          <span 
            onClick={() => setShowBetaExplanation(!showBetaExplanation)}
            className="text-xs text-gray-500 cursor-pointer hover:text-gray-700 transition-colors"
          >
            Beta Version
          </span>
          {showBetaExplanation && (
            <div className="mt-2 text-xs text-gray-500 leading-relaxed">
              This site is currently in beta.
            </div>
          )}
        </div>
        
        {/* Trial Status */}
        {isVerified && userInfoLoading && (
          <div className="mx-2 mb-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <Spinner size="small" />
          </div>
        )}
        {isVerified && !userInfoLoading && userInfo && (
          <TrialStatus 
            trialStatus={trialStatus}
            onUpgradeClick={() => setIsSubscriptionModalOpen(true)}
          />
        )}
        
         {/* Footer Buttons */}
         <div className="p-4 border-t border-gray-300 flex-shrink-0 space-y-2"> 
            <button
              onClick={() => setIsAccountModalOpen(true)}
              className="w-full flex items-center justify-center gap-2 text-gray-700 hover:text-gray-900 hover:bg-gray-300 text-sm font-semibold py-2 px-3 rounded transition-colors" 
            >
              <FiUser size={16} />
              Account
            </button>
            <button
              onClick={() => setIsContactSupportModalOpen(true)}
              className="w-full flex items-center justify-center gap-2 text-gray-700 hover:text-gray-900 hover:bg-gray-300 text-sm font-semibold py-2 px-3 rounded transition-colors"
            >
              <FiMail size={16} />
              Send Feedback
            </button>
            <button
              onClick={handleLogout}
              className="w-full text-red-600 hover:text-red-800 text-sm font-semibold py-2 px-3 rounded transition-colors text-center" 
            >
              Logout
            </button>
         </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col overflow-y-auto md:h-screen-mobile lg:h-screen">
        {isCharactersRoute ? (
          // Character Browser View
          <CharacterBrowser />
        ) : (
          // Chat View
          <>
            {/* Fixed Header */}
            <div className="bg-white shadow-md p-4 flex-shrink-0 z-10"> 
              <div className="max-w-4xl mx-auto w-full flex items-center justify-between">
                <h1 className="font-['Dancing_Script'] text-2xl font-bold text-gray-900 text-center flex-1">
                  {selectedCharacter?.name || 'Chat'}
                </h1>
                
                {/* Header Action Buttons */}
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => navigate('/characters')}
                    className="p-2 text-gray-500 hover:text-purple-600 transition-colors flex items-center gap-1 text-sm"
                    title="Browse Characters"
                  >
                    <FiUser size={16} />
                    <span className="hidden sm:inline">Browse</span>
                  </button>

                  <button
                    onClick={() => setIsHistoryModalOpen(true)}
                    className="p-2 text-gray-500 hover:text-blue-600 transition-colors flex items-center gap-1 text-sm"
                    title="Chat History"
                  >
                    <FiClock size={16} />
                    <span className="hidden sm:inline">History</span>
                  </button>

                  {/* Reset Chat Button */}
                  {currentConversationId && (
                    <button
                      onClick={handleResetChat}
                      className="p-2 text-gray-500 hover:text-red-600 transition-colors flex items-center gap-1 text-sm"
                      title="Reset Chat"
                    >
                      <FiRefreshCw size={16} />
                      <span className="hidden sm:inline">Reset</span>
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Chat Content Area */}
            <div ref={scrollContainerRef} className="chat-scroll flex-1 overflow-y-auto p-4">
              <div className="max-w-4xl mx-auto w-full flex flex-col h-full">
                {/* Verification Prompt */}
                {!isVerified && (
                  <div className="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 p-4 mb-4 rounded-md shadow flex-shrink-0" role="alert">
                    <p className="font-bold">Verify Your Email</p>
                    <p className="text-sm mb-2">Please check your email inbox for a verification link.</p>
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2 mt-2">
                      <button onClick={handleVerificationCheck} disabled={isCheckingVerification} className="bg-bela-600 hover:bg-bela-700 text-white text-sm font-semibold py-1 px-3 rounded">
                        {isCheckingVerification ? 'Checking...' : 'I have verified my email'}
                      </button>
                      <button onClick={handleResendVerification} disabled={resendStatus.loading} className="text-sm text-bela-700 hover:text-bela-900 font-medium">
                        {resendStatus.loading ? 'Sending...' : 'Resend verification email'}
                      </button>
                    </div>
                  </div>
                )}

                {/* Trial Expiry Notice */}
                {isVerified && !userInfoLoading && !trialStatus.canUseChat && (
                  <TrialExpiredMessage 
                    onUpgradeClick={() => setIsSubscriptionModalOpen(true)}
                    variant="inline"
                    trialDurationDays={trialStatus.trialDurationDays}
                  />
                )}

                {/* Message List */}
                <div className={`mb-4 space-y-4 ${!isVerified ? 'opacity-50 pointer-events-none' : ''}`}>
                  {/* Top sentinel — triggers loading of older messages on scroll */}
                  {hasMoreMessages && (
                    <div ref={topSentinelRef} className="flex justify-center py-2">
                      {isLoadingMoreMessages
                        ? <div className="w-5 h-5 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" />
                        : <div className="text-xs text-gray-400">Scroll up for older messages</div>
                      }
                    </div>
                  )}
                  {isLoadingMessages && isVerified && (
                       <div className="flex justify-center items-center h-full">
                          <Spinner /> <span>Loading messages...</span>
                       </div>
                  )}
                  {!isLoadingMessages && messages.length === 0 && isVerified && (
                      <div className="text-center text-gray-500 mt-8">
                          <p>Start a new conversation!</p>
                      </div>
                  )}
                  {!isLoadingMessages && conversationError && isVerified && (
                       <p className="text-red-500 text-center mt-8">{conversationError}</p>
                  )}

                  {!isLoadingMessages && messages.map((msg, index) => (
                    <div
                      key={index}
                      className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start items-start gap-2'}`}
                    >
                      {msg.role !== 'user' && (
                        <div className="w-10 h-10 rounded-full flex-shrink-0 mt-1 overflow-hidden bg-gray-200 cursor-pointer" onClick={() => handleAvatarClick(selectedCharacterId)}>
                           <div className="w-full h-full flex items-center justify-center text-gray-500">
                               {selectedCharacterAvatarUrl ? (
                                 <img src={selectedCharacterAvatarUrl} alt={selectedCharacter?.name || 'Character'} className="w-full h-full object-cover" />
                               ) : <FiUser />}
                             </div>
                        </div>
                      )}
                      <div
                        className={`max-w-[80%] rounded-lg p-3 shadow ${
                          msg.role === 'user'
                            ? 'bg-white text-gray-800 rounded-br-none'
                            : 'bg-rose-50 text-gray-800 rounded-tl-none'
                        }`}
                      >
                        {msg.content && (
                          index === typingMsgIndex ? (
                            <TypingMessage
                              content={msg.content}
                              className="whitespace-pre-wrap text-sm"
                              onDone={() => setTypingMsgIndex(null)}
                            />
                          ) : (
                            <p className="whitespace-pre-wrap text-sm">{msg.content}</p>
                          )
                        )}
                        {Array.isArray(msg.attachments) && msg.attachments.length > 0 && (
                          <div className={`flex flex-col gap-2 items-start ${msg.content ? 'mt-3' : ''}`}>
                            {msg.attachments
                              .filter((attachment) => attachment?.type === 'image' && attachment?.url)
                              .map((attachment) => (
                                <button
                                  key={attachment.id || attachment.url}
                                  type="button"
                                  onClick={() => handleGeneratedImageClick(msg.attachments, `${selectedCharacter?.name || 'Character'} generated image`)}
                                  className="overflow-hidden rounded-lg border border-rose-100 bg-white hover:opacity-95 transition-opacity"
                                >
                                  <img
                                    src={attachment.url}
                                    alt={attachment.fileName || 'Generated image'}
                                    className="max-w-xs max-h-96 object-contain"
                                  />
                                </button>
                              ))}
                            {msg.metadata?.imageCredits && (
                              <p className="text-xs text-gray-400 mt-1 text-right">
                                {msg.metadata.imageCredits.used} / {msg.metadata.imageCredits.limit} images used
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}

                  {isLoading && isVerified && (
                    <div className="flex justify-start items-start gap-2">
                       <div className="w-12 h-12 rounded-full flex-shrink-0 mt-1 overflow-hidden bg-gray-200">
                           {selectedCharacterAvatarUrl ? (
                             <img src={selectedCharacterAvatarUrl} alt={selectedCharacter?.name || 'Character'} className="w-full h-full object-cover" />
                           ) : (
                             <div className="w-full h-full flex items-center justify-center text-gray-500"><FiUser /></div>
                           )}
                       </div>
                      <div className="bg-rose-50 text-gray-800 rounded-lg rounded-tl-none px-4 py-3 shadow flex items-center">
                        <div className="typing-dots" aria-label="Character is typing" role="status">
                          <span className="typing-dot"></span>
                          <span className="typing-dot"></span>
                          <span className="typing-dot"></span>
                        </div>
                      </div>
                    </div>
                  )}

                  <div ref={messagesEndRef} />
                </div>
              </div>
            </div>

            {/* Input Form Area */}
            <div className="bg-gray-100 p-4 border-t border-gray-300 flex-shrink-0">
               <form onSubmit={handleSendMessage} className="flex gap-2 max-w-4xl mx-auto w-full">
                <textarea
                  ref={inputRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  disabled={isLoading || !isVerified || isLoadingMessages || userInfoLoading || !trialStatus.canUseChat} 
                  placeholder={
                    !isVerified 
                      ? "Please verify your email to chat"
                      : !trialStatus.canUseChat
                      ? "Trial expired - upgrade to continue chatting"
                      : "Type a message..."
                  }
                  className={`flex-1 p-3 rounded-lg border border-gray-300 focus:outline-none focus:border-bela-500 resize-none min-h-[50px] max-h-[150px] overflow-y-auto text-sm ${
                    !isVerified || isLoadingMessages || userInfoLoading || !trialStatus.canUseChat 
                      ? 'bg-gray-200 cursor-not-allowed' 
                      : 'bg-white'
                  }`}
                  rows={1}
                  style={{ height: 'auto' }} 
                />
                <button
                  type="submit"
                  disabled={isLoading || !input.trim() || !isVerified || isLoadingMessages || userInfoLoading || !trialStatus.canUseChat} 
                  className="bg-bela-600 hover:bg-bela-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors disabled:opacity-50 self-end text-sm"
                >
                  Send
                </button>
              </form>
            </div>
          </>
        )}
      </div>
      
      {/* Modals */}
      <AccountModal isOpen={isAccountModalOpen} onClose={() => setIsAccountModalOpen(false)} onUpgradeClick={() => { setIsAccountModalOpen(false); setIsSubscriptionModalOpen(true); }} appConfig={appConfig} />
      <SubscriptionModal isOpen={isSubscriptionModalOpen} onClose={() => setIsSubscriptionModalOpen(false)} trialStatus={trialStatus} appConfig={appConfig} />
      <ContactSupportModal isOpen={isContactSupportModalOpen} onClose={() => setIsContactSupportModalOpen(false)} firebaseUser={firebaseUser} />
      <WelcomeModal isOpen={isWelcomeModalOpen} onClose={() => setIsWelcomeModalOpen(false)} isWaitlistUser={trialStatus.isWaitlistUser} />
      <AvatarModal 
        isOpen={isAvatarModalOpen} 
        onClose={() => setIsAvatarModalOpen(false)} 
        imageUrl={avatarModalData.imageUrl}
        images={avatarModalData.images}
        altText={avatarModalData.altText}
      />
      <HistoryModal
        isOpen={isHistoryModalOpen}
        onClose={() => setIsHistoryModalOpen(false)}
        characterId={selectedCharacter?.characterId || null}
        characterName={selectedCharacter?.name || null}
        firebaseUser={firebaseUser}
        onSelect={handleHistorySelect}
        currentConversationId={currentConversationId}
        refreshKey={historyRefreshKey}
      />

      {/* Character Gallery — opened by clicking a character avatar */}
      <CharacterGallery
        isOpen={isGalleryOpen}
        onClose={() => setIsGalleryOpen(false)}
        character={galleryCharacter}
        firebaseUser={firebaseUser}
      />

      {/* New-chat confirmation dialog */}
      {showNewChatDialog && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm p-6 space-y-4">
            <h3 className="text-lg font-semibold text-gray-800">Start a new conversation?</h3>
            <p className="text-sm text-gray-500">
              Would you like to save the current chat to history or discard it?
            </p>
            <div className="flex flex-col gap-2 pt-1">
              <button
                onClick={() => handleNewChatConfirm('save')}
                disabled={isArchiving}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors disabled:opacity-50 text-sm"
              >
                {isArchiving ? 'Saving…' : 'Save to History'}
              </button>
              <button
                onClick={() => handleNewChatConfirm('discard')}
                disabled={isArchiving}
                className="w-full bg-red-50 hover:bg-red-100 text-red-600 font-medium py-2 px-4 rounded-lg transition-colors disabled:opacity-50 text-sm border border-red-200"
              >
                Discard
              </button>
              <button
                onClick={() => handleNewChatConfirm('cancel')}
                disabled={isArchiving}
                className="w-full text-gray-500 hover:text-gray-700 font-medium py-2 px-4 rounded-lg transition-colors disabled:opacity-50 text-sm"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      
      <CharacterModal 
        isOpen={isCharacterModalOpen} 
        onClose={() => setIsCharacterModalOpen(false)} 
        onCharacterCreated={handleCharacterCreated}
        onCharacterUpdated={handleCharacterUpdated}
        appConfig={appConfig}
        trialStatus={trialStatus}
        characterCount={characters.length}
        characterToEdit={characterToEdit}
      />

      <DeleteCharacterModal
        isOpen={!!deleteConfirmChar}
        character={deleteConfirmChar}
        onConfirm={handleConfirmDeleteCharacter}
        onCancel={() => setDeleteConfirmChar(null)}
        isDeleting={isDeletingChar}
      />
    </div>
  );
};

export default Chat;
